import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Button } from "./Button";
import { SpinnerIcon } from "../assets/SpinnerIcon";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { ExclamationTriangleIcon, ExclamationCircleIcon, InformationCircleIcon, XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
export function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel", variant = "danger", isLoading = false, error = null, success = false, successMessage = "Success!", closeOnConfirm = true, afterLeave }) {
    const [showLoading, setShowLoading] = useState(false);
    useEffect(()=>{
        if (!isLoading) {
            setShowLoading(false);
            return;
        }
        const timer = setTimeout(()=>setShowLoading(true), 200);
        const handleBeforeUnload = (e)=>{
            e.preventDefault();
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return ()=>{
            clearTimeout(timer);
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [
        isLoading
    ]);
    const icon = success ? /*#__PURE__*/ _jsx(CheckCircleIcon, {
        className: "h-6 w-6 text-green-500"
    }) : error ? /*#__PURE__*/ _jsx(ExclamationCircleIcon, {
        className: "h-6 w-6 text-red-500"
    }) : variant === "danger" ? /*#__PURE__*/ _jsx(ExclamationTriangleIcon, {
        className: "h-6 w-6 text-red-500"
    }) : variant === "info" ? /*#__PURE__*/ _jsx(InformationCircleIcon, {
        className: "h-6 w-6 text-blue-500"
    }) : null;
    const header = success ? "Success!" : error ? "Error!" : title;
    const body = success ? successMessage : error ? error : message;
    const confirmColor = variant === "danger" ? "red" : "navy-90";
    const cancelColor = variant === "danger" ? "navy-90" : "red";
    const renderButtons = ()=>{
        if (error || success) {
            return /*#__PURE__*/ _jsx(Button, {
                type: "button",
                variant: "simple",
                color: "navy-90",
                onClick: onClose,
                children: "Close"
            });
        }
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    type: "button",
                    variant: "simple",
                    color: cancelColor,
                    onClick: onClose,
                    disabled: showLoading,
                    children: cancelText
                }),
                /*#__PURE__*/ _jsx(Button, {
                    type: "button",
                    variant: "simple",
                    color: confirmColor,
                    disabled: showLoading,
                    onClick: ()=>{
                        onConfirm();
                        if (closeOnConfirm) {
                            onClose();
                        }
                    },
                    children: showLoading ? /*#__PURE__*/ _jsx(SpinnerIcon, {
                        className: "fill-white"
                    }) : confirmText
                })
            ]
        });
    };
    return /*#__PURE__*/ _jsx(Transition, {
        appear: true,
        show: isOpen,
        ...afterLeave && {
            afterLeave
        },
        children: /*#__PURE__*/ _jsxs(Dialog, {
            className: "relative z-50",
            onClose: isLoading ? ()=>{} : onClose,
            children: [
                /*#__PURE__*/ _jsx(TransitionChild, {
                    enter: "ease-out duration-300",
                    enterFrom: "opacity-0",
                    enterTo: "opacity-100",
                    leave: "ease-in duration-200",
                    leaveFrom: "opacity-100",
                    leaveTo: "opacity-0",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "fixed inset-0 bg-black bg-opacity-50"
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "fixed inset-0 overflow-y-auto",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "flex min-h-full items-center justify-center p-4 text-center",
                        children: /*#__PURE__*/ _jsx(TransitionChild, {
                            enter: "ease-out duration-300",
                            enterFrom: "opacity-0 scale-95",
                            enterTo: "opacity-100 scale-100",
                            leave: "ease-in duration-200",
                            leaveFrom: "opacity-100 scale-100",
                            leaveTo: "opacity-0 scale-95 pointer-events-none",
                            children: /*#__PURE__*/ _jsxs(DialogPanel, {
                                className: "w-full max-w-md transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-between mb-4",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center space-x-3",
                                                children: [
                                                    icon,
                                                    /*#__PURE__*/ _jsx(DialogTitle, {
                                                        as: "h3",
                                                        className: "text-lg font-semibold text-gray-900",
                                                        children: header
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                onClick: onClose,
                                                disabled: showLoading,
                                                className: "p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed",
                                                children: /*#__PURE__*/ _jsx(XMarkIcon, {
                                                    className: "h-5 w-5"
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-gray-600",
                                        style: {
                                            whiteSpace: "pre-wrap"
                                        },
                                        children: body
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex justify-end space-x-2 mt-6",
                                        children: renderButtons()
                                    })
                                ]
                            })
                        })
                    })
                })
            ]
        })
    });
}
